import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Gift, Clock, MapPin, Trophy, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyEntries = () => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEntries();
  }, [token]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/entries/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-entries">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>My Entries</h1>
          <p className="text-neutral-600 mt-1">Track all your giveaway entries</p>
        </div>
        <Link to="/giveaways">
          <Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
            Browse More
          </Button>
        </Link>
      </div>

      {entries.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border border-neutral-200 text-center">
          <Gift className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Entries Yet</h3>
          <p className="text-neutral-500 mb-6">Start entering giveaways to win amazing home upgrades!</p>
          <Link to="/giveaways">
            <Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
              Browse Giveaways
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {entries.map((entry, index) => (
            <motion.div
              key={entry.entry_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="relative h-40">
                <img
                  src={entry.giveaway?.image_url || 'https://images.unsplash.com/photo-1772567732969-c1506edf80a0'}
                  alt={entry.giveaway?.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 right-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    entry.giveaway?.status === 'active' 
                      ? 'bg-green-500 text-white' 
                      : entry.giveaway?.status === 'fulfilled'
                      ? 'bg-blue-500 text-white'
                      : 'bg-neutral-500 text-white'
                  }`}>
                    {entry.giveaway?.status?.charAt(0).toUpperCase() + entry.giveaway?.status?.slice(1) || 'Active'}
                  </span>
                </div>
                <div className="absolute bottom-3 left-3">
                  <span className="bg-black/70 text-white font-bold px-3 py-1 rounded-full text-sm">
                    {formatValue(entry.giveaway?.value || 0)}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <h3 className="font-semibold text-lg text-neutral-900 mb-2 line-clamp-1">
                  {entry.giveaway?.title || 'Giveaway'}
                </h3>
                
                <div className="flex items-center gap-4 text-sm text-neutral-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>Entered {new Date(entry.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                  <div className="flex items-center gap-2">
                    {entry.giveaway?.status === 'active' ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-sm text-green-600 font-medium">In Progress</span>
                      </>
                    ) : entry.giveaway?.winner_id === entry.user_id ? (
                      <>
                        <Trophy className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-amber-600 font-medium">Winner!</span>
                      </>
                    ) : (
                      <span className="text-sm text-neutral-500">Ended</span>
                    )}
                  </div>
                  <Link to={`/giveaways/${entry.giveaway_id}`} className="text-[#FF3B30] text-sm font-medium hover:underline flex items-center gap-1">
                    View Details
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyEntries;
